/**
 * Shared logic for Pool-Fahrzeug-Status-Widget and Fleet-KPI-Widget.
 * Issue #1017: Dashboard-Widget: Poolfahrzeug-Status + KPIs Gesamtflotte
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PoolReservierungMinimal {
  beginn: string | null;
  ende: string | null;
  zweck: string | null;
}

export type PoolStatusTyp = 'frei' | 'belegt' | 'reserviert';

export interface PoolStatusInfo {
  typ: PoolStatusTyp;
  bisWann?: string;
  abWann?: string;
  zweck?: string;
}

export interface FlotteFahrzeugInfo {
  id: string;
  status: string;
  anschaffungsdatum: string | null;
  hu_naechste: string | null;
  laufzeitende: string | null;
}

export interface FleetKPIs {
  anzahlAktiv: number;
  durchschnittsalterJahre: number;
  huFaellig90Tage: number;
  laufzeitendeFaellig120Tage: number;
}

// ─── Pool-Status Helper ───────────────────────────────────────────────────────

export function getPoolFahrzeugStatus(
  aktuelleReservierung: PoolReservierungMinimal | null,
  naechsteReservierung: PoolReservierungMinimal | null,
): PoolStatusInfo {
  const now = Date.now();

  if (
    aktuelleReservierung?.beginn &&
    aktuelleReservierung?.ende &&
    now >= new Date(aktuelleReservierung.beginn).getTime() &&
    now < new Date(aktuelleReservierung.ende).getTime()
  ) {
    return {
      typ: 'belegt',
      bisWann: aktuelleReservierung.ende,
      zweck: aktuelleReservierung.zweck ?? undefined,
    };
  }

  if (
    naechsteReservierung?.beginn &&
    new Date(naechsteReservierung.beginn).getTime() > now
  ) {
    return {
      typ: 'reserviert',
      abWann: naechsteReservierung.beginn,
      zweck: naechsteReservierung.zweck ?? undefined,
    };
  }

  return { typ: 'frei' };
}

// ─── Date Formatting ─────────────────────────────────────────────────────────

export function formatDatumKurz(iso: string | null): string {
  if (!iso) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

// ─── Fleet KPI Calculations ──────────────────────────────────────────────────

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_YEAR = MS_PER_DAY * 365.25;

export function berechneFleetKPIs(
  fahrzeuge: FlotteFahrzeugInfo[],
  heute: Date,
): FleetKPIs {
  const aktive = fahrzeuge.filter((f) => f.status === 'aktiv');
  const in90Days = new Date(heute.getTime() + 90 * MS_PER_DAY);
  const in120Days = new Date(heute.getTime() + 120 * MS_PER_DAY);

  const huFaellig90Tage = aktive.filter((f) => {
    if (!f.hu_naechste) return false;
    const hu = new Date(f.hu_naechste);
    return hu >= heute && hu <= in90Days;
  }).length;

  const laufzeitendeFaellig120Tage = aktive.filter((f) => {
    if (!f.laufzeitende) return false;
    const lz = new Date(f.laufzeitende);
    return lz >= heute && lz <= in120Days;
  }).length;

  const mitDatum = aktive.filter((f) => !!f.anschaffungsdatum);
  const durchschnittsalterJahre =
    mitDatum.length === 0
      ? 0
      : mitDatum.reduce((sum, f) => {
          const alter =
            (heute.getTime() - new Date(f.anschaffungsdatum!).getTime()) /
            MS_PER_YEAR;
          return sum + alter;
        }, 0) / mitDatum.length;

  return {
    anzahlAktiv: aktive.length,
    durchschnittsalterJahre,
    huFaellig90Tage,
    laufzeitendeFaellig120Tage,
  };
}
